/**
 * Color math for the palette editor.
 *
 * ── Why this is in the package and the modal is not ──
 *
 * The modal is built on an app's own Dialog, Button and Input, so it has to
 * live in the app. None of THIS does: converting a hex to HSV or scoring a
 * contrast ratio is the same arithmetic wherever it runs, and it is exactly the
 * kind of code that gets pasted twice and then drifts — one copy rounding CMYK,
 * the other truncating it.
 *
 * ── A note on CMYK ──
 *
 * The conversion here is the naive RGB one. It is a useful sanity check for a
 * designer eyeballing an ink mix, and it is NOT a color-managed conversion —
 * no profile, no rendering intent. Anything print-critical comes off a Pantone
 * reference, which the backend does not store yet (see the package README).
 * Label it "approx." wherever it is shown.
 */

export type Rgb = [r: number, g: number, b: number];
/** Hue 0–360, saturation 0–1, value 0–1. */
export type Hsv = [h: number, s: number, v: number];

const HEX_PATTERN = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

export function isHex(value: string): boolean {
	return HEX_PATTERN.test(value.trim());
}

/** `#abc` and `abcdef` alike come back as `#AABBCC`. */
export function normalizeHex(value: string): string {
	let body = value.trim().replace('#', '').toUpperCase();
	if (body.length === 3) {
		body = body
			.split('')
			.map((c) => c + c)
			.join('');
	}
	return `#${body}`;
}

/**
 * Whatever a designer pasted, as a hex — or null.
 *
 * `rgb()` is accepted because that is what the browser inspector and half the
 * design tools put on the clipboard, and retyping it as hex by hand is where a
 * digit gets dropped.
 */
export function parseColor(raw: string): string | null {
	const text = raw.trim();
	const rgb = /^rgba?\(\s*(\d{1,3})[\s,]+(\d{1,3})[\s,]+(\d{1,3})/i.exec(text);
	if (rgb) return rgbToHex([Number(rgb[1]), Number(rgb[2]), Number(rgb[3])]);
	return isHex(text) ? normalizeHex(text) : null;
}

export function hexToRgb(hex: string): Rgb {
	const n = parseInt(normalizeHex(hex).slice(1), 16);
	return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rgbToHex([r, g, b]: Rgb): string {
	return `#${[r, g, b]
		.map((v) =>
			Math.max(0, Math.min(255, Math.round(v)))
				.toString(16)
				.padStart(2, '0')
		)
		.join('')
		.toUpperCase()}`;
}

export function rgbToHsv([r, g, b]: Rgb): Hsv {
	const rn = r / 255;
	const gn = g / 255;
	const bn = b / 255;
	const max = Math.max(rn, gn, bn);
	const min = Math.min(rn, gn, bn);
	const delta = max - min;

	let hue = 0;
	if (delta) {
		if (max === rn) hue = ((gn - bn) / delta) % 6;
		else if (max === gn) hue = (bn - rn) / delta + 2;
		else hue = (rn - gn) / delta + 4;
	}

	return [(hue * 60 + 360) % 360, max ? delta / max : 0, max];
}

export function hsvToHex([h, s, v]: Hsv): string {
	const channel = (n: number) => {
		const k = (n + h / 60) % 6;
		return (v - v * s * Math.max(0, Math.min(k, 4 - k, 1))) * 255;
	};
	return rgbToHex([channel(5), channel(3), channel(1)]);
}

/** Percent cyan/magenta/yellow/key. Naive — see the file header. */
export function rgbToCmyk([r, g, b]: Rgb): [number, number, number, number] {
	const rn = r / 255;
	const gn = g / 255;
	const bn = b / 255;
	const k = 1 - Math.max(rn, gn, bn);
	if (k === 1) return [0, 0, 0, 100];
	return [
		Math.round(((1 - rn - k) / (1 - k)) * 100),
		Math.round(((1 - gn - k) / (1 - k)) * 100),
		Math.round(((1 - bn - k) / (1 - k)) * 100),
		Math.round(k * 100),
	];
}

/** WCAG relative luminance. */
function luminance(hex: string): number {
	const weights = [0.2126, 0.7152, 0.0722];
	return hexToRgb(hex)
		.map((c) => {
			const channel = c / 255;
			return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
		})
		.reduce((sum, channel, i) => sum + channel * weights[i], 0);
}

/** WCAG contrast ratio, 1–21. */
export function contrastRatio(a: string, b: string): number {
	const x = luminance(a);
	const y = luminance(b);
	return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

export type ContrastGrade = 'AAA' | 'AA' | 'AA large' | 'Fail';

export function contrastGrade(ratio: number): ContrastGrade {
	if (ratio >= 7) return 'AAA';
	if (ratio >= 4.5) return 'AA';
	if (ratio >= 3) return 'AA large';
	return 'Fail';
}

/**
 * Black or white, whichever is legible ON this color.
 *
 * Used for the preview tile's own type, so a pale brand color does not print
 * white text on cream and a navy one does not print black on navy.
 */
export function readableInk(hex: string, light = '#FFFFFF', dark = '#141414'): string {
	return contrastRatio(hex, light) > contrastRatio(hex, dark) ? light : dark;
}
